import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import _ from 'lodash'
import VuexLocalStorage from "./persist"
import { formatISO9075 } from 'date-fns'
import { calculateExtraPrice } from "./calculations";

// This function stores the number in localstorage so we "never" get the same number
// two times in a row, this works across page refresh, the number just has to be unique
// in the one booking it is used, therefore a incrementing number is fair enough
const generateUniqueTravellerId = () => {
    let lastNumber = localStorage.getItem("last_number");
    let nextNumber = lastNumber ? parseInt(lastNumber) + 1 : 1;
    localStorage.setItem("last_number", nextNumber);
    return nextNumber;
};

// Used to generate unique ids in the frontend
const uuidv4 = require('uuid/v4');

Vue.use(Vuex);

export default new Vuex.Store({
    state: {
        tourStartDateId: null,
        tourId: null,

        // After submit
        orderReference: null,

        // Other
        bookingData: null,
        isReady: false,
        isProductDataReady: false,
        failedLoadingData: false,

        customer: {
            id: generateUniqueTravellerId(),
            firstName: null,
            lastName: null,
            birthday: null,
            gender: null,
            mobile: null,
            email: null,
            streetAddress: null,
            zip: null,
            city: null
        },
        travellers: [
            // By default, Vitus wants the default number of travellers to be 2, this means we have to pre-fill
            // this array with a traveler object, meaning a unique ID, the rest of the fields
            // will be filled automatically when filling out the TravellerForm
            {
                id: generateUniqueTravellerId(),
                firstName: null,
                lastName: null,
                birthday: null,
                gender: null,
            }
        ],

        isRoomSelectionDirty: false,
        rooms: {},
        productData: [],
        selectedExtras: [],
        selectedRoomExtras: [],
        agreedToTermsOfService: false,
        agreedToMarketing: false,
        isValidated: false,
        roomAssignments: [],

        roomsOrTravellersHaveChanged: false,
        selectedPaymentMethod: "postpone",

        // Booking states
        isReservingBooking: false,
        bookingFailed: false,
        bookingSubmitted: false,
    },
    mutations: {
        restoreState() {
            // Used to trigger restoration of state from local storage
        },

        setIsReservingBooking(state, data) {
            state.isReservingBooking = data;
        },
        setFailedLoadingData(state, data) {
            state.failedLoadingData = data;
        },
        setBookingSubmitted(state, data) {
            state.bookingSubmitted = data;
        },
        setBookingFailed(state, data) {
            state.bookingFailed = data;
        },
        setOrderReference(state, data) {
            state.orderReference = data;
        },
        updateSelectedPaymentMethod(state, paymentMethod) {
            state.selectedPaymentMethod = paymentMethod;
        },
        setTourStartDateId(state, tourStartDateId) {
            state.tourStartDateId = tourStartDateId
        },
        setTourId(state, tourId) {
            state.tourId = tourId
        },
        setInitialBookingData(state, bookingData) {
            state.bookingData = bookingData
        },
        setReady(state, ready) {
            state.isReady = ready
        },
        updateCustomer(state, data) {
            state.customer = data
        },
        updateTraveller(state, data) {

            const key = data.number - 1;

            Vue.set(state.travellers, key, {
                // Fixes bug, where when you updated a traveler the id was overriden.
                id: state.travellers[key].id,
                ...data.traveller
            });
        },
        addTraveller(state) {
            state.travellers.push({
                id: generateUniqueTravellerId(),
                firstName: null,
                lastName: null,
                birthday: null,
                gender: null,
            });
        },
        removeTraveller(state) {
            let traveller = state.travellers.pop();
            // Clear out the selected Extras on the person
            state.selectedExtras = state.selectedExtras.filter(se => se.personId !== traveller.id)

            // Remove the traveller from all room assignments
            state.roomAssignments = state.roomAssignments.map(ra => {
                return {
                    ...ra,
                    people: ra.people.filter(p => p !== traveller.id)
                }
            });

            state.roomsOrTravellersHaveChanged = true;
        },
        updateRoomSelection(state, data) {

            state.isRoomSelectionDirty = true;

            // This code creates a structure like this:
            // rooms: {
            //  SOME-UUID-HERE-1: [ROOM_1_ID, ROOM_2_ID]
            //  SOME-UUID-HERE-2: [ROOM_3_ID]
            //  SOME-UUID-HERE-3: [ROOM_4_ID]
            // }
            //
            // When the room selection is updated, what we are doing is
            // creating a new UUID that represents "that instance" of the room, or removing that ID,
            //
            // if we setting the room number to 0, we will also room key from the rooms object.
            // All of this is done so each selected room has its own unique ID we can refer to it by.

            if (state.rooms.hasOwnProperty(data.productUnitId)) {
                if (state.rooms[data.productUnitId].length < data.amount) {
                    // We are adding another instance to a room we already have selected
                    state.rooms[data.productUnitId].push(uuidv4())
                } else {
                    // Remove a room
                    state.rooms[data.productUnitId].pop();

                    // Remove the room key from the object if the room is no longer selected
                    if (state.rooms[data.productUnitId].length === 0) {
                        delete state.rooms[data.productUnitId]
                    }
                }
            } else {
                // Room has not been previously selected, create the property with a new UUID
                Vue.set(state.rooms, data.productUnitId, [uuidv4()])
            }

            state.roomsOrTravellersHaveChanged = true;
        },
        setProductData(state, productData) {
            state.productData = productData
        },
        setProductDataReady(state, ready) {
            state.isProductDataReady = ready
        },
        updateExtraSelection(state, data) {
            let selectedExtra = state.selectedExtras.find(se => se.personId === data.personId && se.productId === data.productId);
            if (selectedExtra == null) {
                state.selectedExtras.push(data)
            } else {
                selectedExtra.selected = data.selected
            }
        },
        updateRoomExtraSelection(state, data) {
            let selectedRoomExtras = state.selectedRoomExtras.find(se => se.roomId === data.roomId && se.productId === data.productId);
            if (selectedRoomExtras == null) {
                state.selectedRoomExtras.push(data)
            } else {
                selectedRoomExtras.selected = data.selected
            }
        },
        updateAgreedToMarketing(state, data) {
            state.agreedToMarketing = data
        },
        updateAgreedToTermsOfService(state, data) {
            state.agreedToTermsOfService = data
        },
        setValidation(state, data) {
            state.isValidated = data
        },
        updateRoomAssignments(state, data) {
            state.roomAssignments = data;
            state.roomsOrTravellersHaveChanged = false;
        },
    },
    getters: {

        // VALIDATION
        //=========================================================================================================
        // Used for validation on page 1, checks if the selected rooms can fit all the selected travelers.
        canFitAllTravelers(state, getters) {

            let maxCapacityForSelectedRooms = getters.selectedRooms.reduce((total, room) => {
                return total + (room.maxNrOfTravelers * room.amount);
            }, 0);

            let minRequiredCapacityForSelectedRooms = getters.selectedRooms.reduce((total, room) => {
                return total + (room.minNrOfTravelers * room.amount);
            }, 0);

            return (maxCapacityForSelectedRooms >= getters.personList.length)
                    && (minRequiredCapacityForSelectedRooms <= getters.personList.length)
        },

        hasFilledAllTravelerInformation(state) {
            let fieldIsNotEmptyOrNull = field => (field !== null && field !== '');
            let customerIsFilled = Object.values(state.customer).every(fieldIsNotEmptyOrNull);

            // Edge case, check if the email is correct on the customer
            const emailRegex = /(^$|^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$)/;
            if (!emailRegex.test(state.customer.email)) {
                return false;
            }

            let travelersAreFilled = state.travellers.every(traveler => Object.values(traveler).every(fieldIsNotEmptyOrNull));

            return travelersAreFilled && customerIsFilled;
        },

        allTravelersHasBeenAssignedARoom(state, getters) {

            let allRoomsHaveValidNumberOfGuests = state.roomAssignments.every(roomAssignment => {
                // The people count assigned to the rooms should not go below or above the min and max nr of people on that room
                return (roomAssignment.minNrOfPeople <= roomAssignment.people.length)
                        && (roomAssignment.people.length <= roomAssignment.maxNrOfPeople);
            });

            let assignedGuestCount = state.roomAssignments.reduce((guestCount, roomAssignment) => {
                return guestCount + roomAssignment.people.length;
            }, 0);

            let allTravelersHasARoom = assignedGuestCount === getters.numberOfTravellers;

            return allRoomsHaveValidNumberOfGuests && allTravelersHasARoom;
        },

        canAutomaticallyAssignTravelersToRooms(state, getters) {

            let selectedRooms = getters.selectedRooms;
            if (selectedRooms.length === 1) {
                let room = selectedRooms[0];

                // Single room, single traveler
                if (room.minNrOfTravelers === 1 && getters.personList.length === 1) {
                    return true;
                }

                // Double room, 2 travelers
                if (room.minNrOfTravelers === 2 && getters.personList.length === 2) {
                    return true;
                }

            }

            return false;


        },

        //=========================================================================================================

        // Get the first and last flight from the primary flight group, these contain the departure and return dates of the trip
        flights(state) {
            let flightGroup = state.bookingData.tourTemplate.FlightGroups.find((fg => fg.Primary));

            let flights = [];

            flights.push(flightGroup.Flights[0]);
            flights.push(flightGroup.Flights[flightGroup.Flights.length - 1]);

            return flights;
        },


        startDates(state) {

            return state.bookingData.tourSearch[0].StartdateCapacities.map(sc => {
                try {
                    if (Object.values(state.bookingData.flights).length) {
                        let flights = Object.values(state.bookingData.flights).find(f => f.startDateId === sc.TourStartdateId);
                        let departureDate = new Date(Date.parse(flights.departure.FromDateAndTime));
                        // TODO: Should we show the time the flight leaves, or when it lands on the return?
                        let returnDate = new Date(Date.parse(flights.return.ToDateAndTime));
                        let airport = sc.Tag.find(t => t.CategoryId === 3).Name;

                        let date = departureDate.toLocaleDateString("no-NB", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        });

                        let time = departureDate.toLocaleTimeString("no-NB", {
                            hour: '2-digit',
                            minute: '2-digit'
                        });


                        let name = `${ date }, ${ time } (${ airport })`;

                        return {
                            tourStartDateId: sc.TourStartdateId,
                            name: name,
                            days: sc.Days,
                            date: departureDate,
                            displayDate: `${ date }, ${ time }`,
                            returnDate: returnDate,
                            capacity: sc.Capacity,
                            disabled: sc.Capacity === 0,
                            airport: airport ? airport : null,
                            rawDate: flights.departure.FromDateAndTime,
                        }
                    } else {
                        let scDate = new Date(Date.parse(sc.Date));
                        let date = scDate.toLocaleDateString("no-NB", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        });

                        let correctedDate;
                        if (scDate.getTimezoneOffset() < 0) {
                            correctedDate = new Date(scDate.getTime() - scDate.getTimezoneOffset() * 60000);
                        } else {
                            correctedDate = new Date(scDate.getTime() + scDate.getTimezoneOffset() * 60000);
                        }

                        let returnDate = new Date(correctedDate.getTime() + (sc.Days - 1) * 1440 * 60000);
                        return {
                            tourStartDateId: sc.TourStartdateId,
                            name: "Not displayed",
                            days: sc.Days,
                            date: "Not displayed",
                            displayDate: date,
                            returnDate: returnDate,
                            capacity: sc.Capacity,
                            disabled: sc.Capacity === 0,
                            airport: null,
                            rawDate: correctedDate,
                        }
                    }


                } catch (e) {
                    return null;
                }
            })
                    // If for some reason anything fails when calculating the start dates it will return null,
                    // filter these out.. not optimal, but will prevent the entire booking for from stopping to work.
                    .filter(st => st != null)
                    // Sort by start date
                    .sort((a, b) => a.date - b.date);
        },
        selectedStartDate(state, getters) {
            return getters.startDates.find(d => d.tourStartDateId == state.tourStartDateId);
        },
        returnDate(state, getters) {
            return getters.selectedStartDate.returnDate;
        },
        roomList(state) {
            let temporaryRooms = [];
            for (let roomId in state.rooms) {
                for (let id of state.rooms[roomId]) {
                    let temp = state.bookingData.roomSelections.find(ar => ar.Room.ProductUnitId == roomId)
                    temporaryRooms.push({
                        id: id,
                        roomId: roomId,
                        name: temp.Name,
                    })
                }
            }

            return temporaryRooms;
        },
        personList(state) {
            // Basically combines the travellers and customer into a single list for use in the frontend
            let people = [];
            people.push(state.customer);
            for (let traveller of state.travellers) {
                people.push(traveller)
            }

            return people;
        },
        numberOfTravellers(state) {
            return state.travellers.length + 1
        },
        availableRooms(state) {
            if (!state.bookingData) return [];

            return state.bookingData.roomSelections
                    .map(tp => {
                        return {
                            name: tp.Name,
                            price: tp.Price,
                            maxCapacity: tp.Capacity,
                            maxNrOfTravelers: tp.MaxNrOfTravelers,
                            minNrOfTravelers: tp.MinNrOfTravelers,
                            productUnitId: tp.Room.ProductUnitId,
                            unitTypeConditionId: tp.Room.UnitTypeConditionId,
                            twinRoom: tp.Room.TwinRoom
                        };
                    });
        },
        selectedRooms(state, getters) {
            return getters.availableRooms
                    .filter(room => Object.keys(state.rooms).includes(room.productUnitId.toString()))
                    .map(room => {
                        return {
                            ...room,
                            amount: state.rooms[room.productUnitId].length
                        }
                    })
        },
        selectedExtrasWithPrice(state, getters) {
            return state.selectedExtras
                    .filter(se => se.selected)
                    .map(se => {
                        return {
                            ...se,
                            amount: getters.extras.find(e => e.ProductID === se.productId).ProductPeriod.PriceList[0].Amount,
                            raw: getters.extras.find(e => e.ProductID === se.productId)
                        }
                    })
        },
        selectedRoomExtrasWithPrice(state, getters) {
            return state.selectedRoomExtras
                    .filter(se => se.selected)
                    .map(se => {
                        let raw = getters.extrasPerRoom.find(e => e.ProductID === se.productId);

                        let roomProductId = Object.keys(state.rooms).find((id) => state.rooms[id].includes(se.roomId));
                        let room = getters.availableRooms.find(room => room.productUnitId == roomProductId);


                        let prices = calculateExtraPrice(raw, room.minNrOfTravelers);
                        return {
                            ...se,
                            amount: prices.totalPrice,
                            displayPrice: prices.displayPrice,
                            raw: raw,
                            pricePerPerson: !prices.isUnitPrice,
                            personCount: room.minNrOfTravelers
                        }
                    })
        },
        totalPrice(state, getters) {
            let roomPrice = getters.selectedRooms
                    .reduce((result, room) => result + (room.minNrOfTravelers * room.price) * room.amount, 0);

            let extrasPrice = getters.selectedExtrasWithPrice
                    .reduce((result, se) => result + se.amount, 0);

            let roomExtrasPrice = getters.selectedRoomExtrasWithPrice
                    .reduce((result, se) => result + se.amount, 0);

            return roomPrice + extrasPrice + roomExtrasPrice;
        },
        extrasPerRoom(state) {
            return state.bookingData.tourTemplate.TourTemplateDays
                    .filter(tdd => tdd.TourTemplateDayEnum === 14)
                    .filter(ttd => ttd.TourParts.length)
                    .map(ttd => ttd.TourParts
                            .filter(bla => bla.TourPartConditionList.length)
                            .filter(tp => tp.TourPartConditionList[0].ConditionType === "Optional"))
                    .filter(ttd => ttd.length)
                    .flat()
                    .map(p => {
                        return {
                            ...p,
                            raw: state.productData[p.ProductID]
                        }
                    });
        },
        extras(state) {
            return state.bookingData.tourTemplate.TourTemplateDays
                    .filter(tdd => tdd.TourTemplateDayEnum !== 14)
                    .filter(ttd => ttd.TourParts.length)
                    .map(ttd => ttd.TourParts
                            .filter(bla => bla.TourPartConditionList.length)
                            .filter(tp => tp.TourPartConditionList[0].ConditionType === "Optional"))
                    .filter(ttd => ttd.length)
                    .flat()
                    .map(p => {
                        return {
                            ...p,
                            raw: state.productData[p.ProductID]
                        }
                    });
        },
        selectedExtrasSidebarRoom(state, getters) {
            return _.groupBy([
                ...getters.selectedRoomExtrasWithPrice,
            ], 'productId')
        },
        selectedExtrasSidebarPerson(state, getters) {
            return _.groupBy([
                ...getters.selectedExtrasWithPrice,
            ], 'productId')
        },
        hasAnyExtras(state, getters) {
            return Object.entries(
                    _.groupBy([
                        ...getters.selectedExtrasWithPrice,
                        ...getters.selectedRoomExtrasWithPrice,
                    ], 'productId')
            ).length > 0;
        },
        productIds(state) {
            return state.bookingData.tourTemplate.TourTemplateDays
                    .filter(ttd => ttd.TourParts.length)
                    .map(ttd => ttd.TourParts
                            .filter(bla => bla.TourPartConditionList.length)
                            .filter(tp => tp.TourPartConditionList[0].ConditionType === "Optional"))
                    .filter(e => e.length)
                    .map(e => e.map(ee => ee.ProductID))
                    .flat()
        },


        depositAmount(state, getters) {
            if (!state.bookingData.canPayWithInvoice) {
                return 0;
            }

            let amount = state.bookingData.hasDepositumPerPerson
                    ? Math.max(state.bookingData.depositumMimimumSum * getters.personList.length, state.bookingData.depositumPercent * getters.totalPrice)
                    : Math.max(state.bookingData.depositumMimimumSum, state.bookingData.depositumPercent * getters.totalPrice);

            return Math.round(amount);
        },

        bookingSubmissionData(state, getters) {
            const travellerAdultType = 1;

            return {
                "TourStartdateId": state.tourStartDateId,
                "StartDate": getters.selectedStartDate.rawDate,
                "TourType": "grouptravel",
                "Culture": "nb-NO",
                "Customer": {
                    "FirstName": state.customer.firstName,
                    "LastName": state.customer.lastName,
                    "Phone": state.customer.mobile,
                    "Email": state.customer.email,
                    "MobilePhone": state.customer.mobile,
                    "Address": {
                        "AddressLine1": state.customer.streetAddress,
                        "City": state.customer.city,
                        "PostalCode": state.customer.zip,
                        "Country": "Norge"
                    },
                    "NewsLetter": state.agreedToMarketing,
                    "AgreedToGdpr": state.agreedToTermsOfService,
                    "AgreeToOffersAndInformation": state.agreedToMarketing
                },
                "Rooms": [
                    ...state.roomAssignments.map(room => {
                        let theRoom = getters.selectedRooms.find(sr => sr.productUnitId == room.roomId);
                        let temporary = state.bookingData.roomSelections.find(r => r.Room.ProductUnitId == theRoom.productUnitId);


                        return {
                            "Travelers": room.people.map(personId => {
                                        let person = getters.personList.find(p => p.id == personId);

                                        let date = person.birthday;
                                        if (typeof date === "string") {
                                            // When the date is retrieved from localStore it is stored as a string,
                                            // This happens when the user cancels the payment and goes back to this page. or if they refresh the page during hte booking process
                                            date = new Date(Date.parse(date));
                                        }

                                        // Since Javascript Dates are stupid, let a competent library do the formatting instead,
                                        // this creates a MySQL formatted date that has no timezone
                                        let formattedDate = formatISO9075(date);

                                        return {
                                            "TravelerId": person.id,
                                            "Type": travellerAdultType,
                                            "FirstName": person.firstName,
                                            "LastName": person.lastName,
                                            "BirthDate": formattedDate,
                                            "GenderId": person.gender === "male" ? 1 : 2, // male = 1, female = 2
                                            "Phone": state.customer.mobile || null,
                                            "MobilePhone": person.mobile || null,
                                            "AddressLine1": person.streetAddress || null,
                                            "AddressCity": person.city || null,
                                            "AddressPostalCode": person.zip || null,
                                            "AddressCountry": "Norway",
                                            "Email": person.email || null,
                                            "NoEmail": false,
                                        }
                                    }
                            ),
                            "TwinRoom": theRoom.twinRoom,
                            "ProductUnitId": theRoom.productUnitId,
                            "FlattenedRoom": temporary.Room.FlattenedRoom,
                            "UnitTypeConditionId": theRoom.unitTypeConditionId,
                            "RoomSelectionConditionId": temporary.Room.RoomSelectionConditionId,

                            // Tillvalg per rom
                            "SelectedActivities": getters.selectedRoomExtrasWithPrice
                                    .filter(sre => sre.roomId === room.id)
                                    .map(sre => {
                                        return {
                                            "TourPartID": sre.raw.TourPartId,
                                            "ProductID": sre.productId,
                                        }
                                    })
                        }
                    }),
                ],

                // Tillvalg per person
                "SelectedActivities":
                        Object.entries(_.groupBy(getters.selectedExtrasWithPrice, 'productId')).map(e => {
                            let productId = e[0];
                            let entries = e[1];

                            return {
                                "TourPartID": entries[0].raw.TourPartId,
                                "ProductID": productId,
                                "Travelers": entries.map(e => e.personId).map(personId => {
                                            let person = getters.personList.find(p => p.id === personId);
                                            return {
                                                "TravelerId": person.id,
                                                "Type": 1,
                                            }
                                        }
                                ),
                            }
                        }),
            }
        }
    },
    actions: {

        redirectToPayment({ state }) {
            if (!state.orderReference) {
                throw new Error("Can't redirect to payment without an order reference");
            }

            const orderId = state.orderReference;


            // Explanation of this weirdness. (aka, rant)
            // =============================================================
            // Screenbooking did not want to expose customer details in their API, so to get the
            // receipt page to display the name of the customer, we have to pass it along via localStorage.
            localStorage.setItem("receipt_" + orderId, JSON.stringify({
                name: `${ state.customer.firstName } ${ state.customer.lastName }`,
                travelers: state.travellers.map(t => `${ t.firstName } ${ t.lastName }`),
                address: state.customer.streetAddress,
                zip: state.customer.zip,
                city: state.customer.city,
                country: "Norge",
            }));

            // This has to be URI Encoded because we can't send a raw URL as a query param, as it will not be parsed correctly be the receiver
            const redirectUrl = `${ window.location.origin }/kvittering/${ orderId }`;

            switch (state.selectedPaymentMethod) {
                case "deposit": {
                    let params = new URLSearchParams({
                        tourId: state.tourId,
                        id: orderId,
                        redirectUrl: redirectUrl,
                        cancelRedirectUrl: window.location,
                        hasDeposit: true,
                        depositPercent: state.bookingData.depositumPercent * 100,
                    });

                    window.location = "https://minside.vitusreiser.no/Payment/Pay/?" + params.toString();
                    break;
                }

                case "full": {
                    let params = new URLSearchParams({
                        tourId: state.tourId,
                        id: orderId,
                        redirectUrl: redirectUrl,
                        cancelRedirectUrl: window.location,
                    });

                    window.location = "https://minside.vitusreiser.no/Payment/Pay/?" + params.toString();
                    break;
                }

                case "postpone":
                    // Decode the redirect URL as we have to use it ourselves here, we skip the payment stage.
                    window.location = redirectUrl;
                    break;
                default:
                    return null;
            }

        },

        submitBooking({ state, commit, dispatch, getters }) {

            let params = new URLSearchParams({
                tourId: state.tourId,
                tourStartdateId: state.tourStartDateId,
                tourTemplateId: state.bookingData.tourTemplateId,
                payingWithInvoice: state.selectedPaymentMethod === "postpone"
            });
            let url = `/actions/booking-module/booking/reserve?${ params.toString() }`;

            // TODO(28 jan. 2020) ~ Helge: These can probably be combined into a commit('setBookingStage', reserving|failed|success)
            commit('setIsReservingBooking', true);
            commit('setBookingSubmitted', false);
            commit('setBookingFailed', false);


            let options = {
                full: 'Betalingskort',
                deposit: 'Depositum',
                postpone: 'Nettbank'
            };

            try {
                window.dataLayer.push({
                    'event': 'checkout',
                    'ecommerce': {
                        'checkout': {
                            'actionField': { 'step': 5 },
                            'products': [{
                                'name': window.gtmName,
                                'id': state.tourId,
                                'price': getters.totalPrice,
                                'category': window.gtmCategory,
                                'variant': window.gtmVariant,
                                'quantity': 1
                            }]
                        }
                    },
                });
                window.dataLayer.push({
                    'event': 'checkoutOption',
                    'ecommerce': {
                        'checkout_option': {
                            'actionField': {
                                'step': 5,
                                'option': options[state.selectedPaymentMethod],
                            }
                        }
                    }
                });
            } catch (e) {
                console.error("GTM Threw an error during checkout: ", e);
            }

            axios.post(url, getters.bookingSubmissionData)
                    .then((response) => {
                        if (response.data.status === "ok") {
                            commit('setIsReservingBooking', false);
                            commit('setBookingFailed', false);
                            commit('setBookingSubmitted', true);
                            commit("setOrderReference", response.data.orderReference);
                            commit('setBookingSubmitted', false);
                            dispatch("redirectToPayment");
                        } else if (response.data.status === "error") {
                            commit('setBookingSubmitted', false);
                            commit('setIsReservingBooking', false);
                            commit('setBookingFailed', true);
                        }
                    })
                    .catch(error => {
                        commit('setBookingSubmitted', false);
                        commit('setIsReservingBooking', false);
                        commit('setBookingFailed', true);
                    });
        },
        refreshData(context) {
            context.dispatch("loadData", {
                tourId: context.state.tourId,
                startDateId: context.state.tourStartDateId
            });
        },
        loadData(context, { tourId, startDateId }) {
            context.commit('setReady', false);
            axios
                    .get('/actions/booking-module/booking/', {
                        params: {
                            tourId: tourId,
                            tourStartDateId: startDateId,
                        }
                    })
                    .then((response) => {
                        context.commit('setInitialBookingData', response.data);

                        // There is a change the provided StartdateId is invalid or old (and won't show in the startDates list anymore,
                        // in this case, just make the form use the failed state.
                        if (context.getters.selectedStartDate) {
                            context.commit('setReady', true);
                            context.commit('setFailedLoadingData', false);
                        } else {
                            context.commit('setReady', false);
                            context.commit('setFailedLoadingData', true);
                        }

                    })
                    .catch(error => {
                        context.commit('setFailedLoadingData', true);
                        context.commit('setReady', false);
                    });
        },
        loadProductData(context) {
            let productIds = context.getters.productIds;
            if (productIds.length) {
                context.commit('setProductDataReady', false);

                axios.get('/actions/booking-module/booking/product-info', {
                    params: {
                        productIds: productIds
                    }
                })
                        .then((response) => {
                            context.commit('setProductData', response.data);
                            context.commit('setProductDataReady', true);
                        });
            } else {
                // If this is not set to true the page3 content is not shown
                context.commit('setProductDataReady', true);
            }
        },

        automaticallyAssignTravelers({ state, commit, getters }) {
            let selectedRooms = getters.selectedRooms;
            if (selectedRooms.length === 1) {
                let room = selectedRooms[0];
                let roomData = state.bookingData.roomSelections.find(rs => rs.Room.ProductUnitId === room.productUnitId);
                let roomId = state.rooms[room.productUnitId][0];

                // Single room, single traveler or  Double room, 2 travelers
                if ((room.minNrOfTravelers === 1 && getters.personList.length === 1) || (room.minNrOfTravelers === 2 && getters.personList.length === 2)) {
                    commit("updateRoomAssignments", [{
                        id: roomId,
                        maxNrOfPeople: roomData.MinNrOfTravelers,
                        minNrOfPeople: roomData.MaxNrOfTravelers,
                        name: roomData.Name,
                        people: getters.personList.map(p => p.id),
                        roomId: room.productUnitId,
                    }]);
                }

            }
        },
    },

    plugins: [
        VuexLocalStorage
    ],
});
